import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Row,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";

import LoadingSpinner from "../../../../Components/Common/Spinner";
import NoDataContent from "../../../../Components/Common/NoDataContent";
import { FetchAPI } from "../../../../helpers/APIdata/fetchAPI";

// Super Admin > Email Outbound (Task 24429).
//
// Platform-level sent mail log (append only)
//
// One-to-one with the MasterEmailOutbound table in ZCRM_Master. Search runs on the server against MessageId, ServiceProviderEmailAddress, SgMessageXId, FromName, To, Cc.
//
// Columns are curated: audit stamps, marker columns and inherited clone
// boilerplate are not shown. See ztools/masteradmin/manifest_part1.php.

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

// Held as a string rather than inlined into the JSX: some descriptions contain
// angle brackets (AUTHCAP<Id>X) which JSX would try to parse as a tag.
const DESCRIPTION = "Platform-level sent mail log (append only)";

const plainCell = (v) =>
  v === null || v === undefined || v === "" ? (
    <span className="text-muted">—</span>
  ) : String(v).length > 60 ? (
    <span title={String(v)}>{`${String(v).slice(0, 60)}…`}</span>
  ) : (
    String(v)
  );

const flagCell = (v) =>
  Number(v) === 1 ? (
    <Badge color="success">Yes</Badge>
  ) : (
    <Badge color="light" className="text-muted">
      No
    </Badge>
  );

const COLUMNS = [
  { key: "Id", cell: plainCell },
  { key: "CreatedDateTime", cell: plainCell },
  { key: "Subject", cell: plainCell },
  { key: "To", cell: plainCell },
  { key: "From", cell: plainCell },
  { key: "StatusCode", cell: plainCell },
];

const FIELDS = [
  { name: "InternalId", type: "integer", required: false, maxLength: null, options: [], hint: "int" },
  { name: "EmailAccount_Id", type: "integer", required: false, maxLength: null, options: [], hint: "int" },
  { name: "StatusCode", type: "integer", required: false, maxLength: null, options: [], hint: "int" },
  { name: "MessageId", type: "text", required: false, maxLength: 100, options: [], hint: "varchar(100)" },
  { name: "ServiceProviderEmailAddress", type: "text", required: false, maxLength: 500, options: [], hint: "varchar(500)" },
  { name: "SgMessageXId", type: "text", required: false, maxLength: 500, options: [], hint: "varchar(500)" },
  { name: "EmailLogInboundStatus_Id", type: "integer", required: false, maxLength: null, options: [], hint: "int" },
  { name: "FromName", type: "text", required: false, maxLength: 500, options: [], hint: "varchar(500)" },
  { name: "To", type: "text", required: false, maxLength: 500, options: [], hint: "varchar(500)" },
  { name: "Cc", type: "text", required: false, maxLength: 500, options: [], hint: "varchar(500)" },
  { name: "Bcc", type: "text", required: false, maxLength: 500, options: [], hint: "varchar(500)" },
  { name: "Subject", type: "text", required: false, maxLength: 500, options: [], hint: "varchar(500)" },
  { name: "BodyPlain", type: "textarea", required: false, maxLength: null, options: [], hint: "longtext" },
  { name: "BodyHtml", type: "textarea", required: false, maxLength: null, options: [], hint: "longtext" },
  { name: "Body", type: "textarea", required: false, maxLength: null, options: [], hint: "longtext" },
  { name: "ReplyTo", type: "text", required: false, maxLength: 500, options: [], hint: "varchar(500)" },
  { name: "From", type: "text", required: false, maxLength: 500, options: [], hint: "varchar(500)" },
  { name: "Log", type: "textarea", required: false, maxLength: null, options: [], hint: "longtext" },
  { name: "Notes", type: "textarea", required: false, maxLength: null, options: [], hint: "longtext" },
  { name: "Company_Id", type: "integer", required: false, maxLength: null, options: [], hint: "int" },
  { name: "Invoice_Id", type: "integer", required: false, maxLength: null, options: [], hint: "int" },
  { name: "Customer_Id", type: "integer", required: false, maxLength: null, options: [], hint: "int" },
  { name: "Job_Id", type: "integer", required: false, maxLength: null, options: [], hint: "int" },
  { name: "NextActionDateTime", type: "datetime", required: false, maxLength: null, options: [], hint: "datetime" },
  { name: "PortalInternalId_Id", type: "integer", required: false, maxLength: null, options: [], hint: "int" },
  { name: "LogError", type: "textarea", required: false, maxLength: null, options: [], hint: "longtext" },
  { name: "Title", type: "text", required: false, maxLength: 500, options: [], hint: "varchar(500)" },
  { name: "EmailLogPriority_Id", type: "integer", required: false, maxLength: null, options: [], hint: "int" },
  { name: "DeliveredEmailQuantity", type: "integer", required: false, maxLength: null, options: [], hint: "int" },
  { name: "DeliveredEmailFirstDateTime", type: "datetime", required: false, maxLength: null, options: [], hint: "datetime" },
  { name: "DeliveredEmailLastDateTime", type: "datetime", required: false, maxLength: null, options: [], hint: "datetime" },
  { name: "OpenedEmailQuantity", type: "integer", required: false, maxLength: null, options: [], hint: "int" },
  { name: "OpenedEmailFirstDateTime", type: "datetime", required: false, maxLength: null, options: [], hint: "datetime" },
  { name: "OpenedEmailLastDateTime", type: "datetime", required: false, maxLength: null, options: [], hint: "datetime" },
  { name: "Staff_Id", type: "integer", required: false, maxLength: null, options: [], hint: "int" },
  { name: "FromEmailAddress", type: "text", required: false, maxLength: 500, options: [], hint: "varchar(500)" },
  { name: "ArchiveDateTime", type: "datetime", required: false, maxLength: null, options: [], hint: "datetime" },
  { name: "ReadDateTime", type: "datetime", required: false, maxLength: null, options: [], hint: "datetime" },
  { name: "EmailDirection", type: "integer", required: false, maxLength: null, options: [], hint: "int" },
  { name: "ToName", type: "text", required: false, maxLength: 500, options: [], hint: "varchar(500)" },
  { name: "ToEmailAddress", type: "text", required: false, maxLength: 500, options: [], hint: "varchar(500)" },
  { name: "PossibleCustomerCount", type: "integer", required: false, maxLength: null, options: [], hint: "int" },
  { name: "PossibleCustomerResultArray", type: "textarea", required: false, maxLength: null, options: [], hint: "longtext" },
  { name: "EmailAddressArray", type: "textarea", required: false, maxLength: null, options: [], hint: "longtext" },
  { name: "ContentType", type: "integer", required: false, maxLength: null, options: [], hint: "tinyint(1)" },
];

const renderInput = (field, value, onChange) => {
  const v = value === null || value === undefined ? "" : value;

  if (field.type === "boolean") {
    return (
      <Input
        type="select"
        value={String(Number(v) === 1 ? 1 : 0)}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="0">No</option>
        <option value="1">Yes</option>
      </Input>
    );
  }

  if (field.type === "enum") {
    return (
      <Input type="select" value={v} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {field.options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </Input>
    );
  }

  if (field.type === "textarea") {
    return (
      <Input
        type="textarea"
        rows={4}
        value={v}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  return (
    <Input
      type={
        field.type === "integer" || field.type === "decimal" ? "number" : "text"
      }
      step={field.type === "decimal" ? "any" : undefined}
      maxLength={field.maxLength || undefined}
      value={v}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

const MasterEmailOutbound = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [search, setSearch] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await FetchAPI("/masteremailoutbound/search", "POST", [
        {
          Search: search.trim(),
          Page: page,
          PageSize: pageSize,
          FlagIncludeDeleted: includeDeleted ? 1 : 0,
        },
      ]);

      if (Number(res?.status) === 200) {
        setRows(res?.masteremailoutbound_list || []);
        setSummary(res?.summary || null);
      } else {
        setRows([]);
        setSummary(null);
        toast.error(res?.message || "Could not load Email Outbound");
      }
    } catch (e) {
      setRows([]);
      setSummary(null);
      toast.error("Could not load Email Outbound");
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search, includeDeleted]);

  // Debounced so typing in the search box fires one request, not one per key.
  useEffect(() => {
    const timer = setTimeout(() => load(), 350);
    return () => clearTimeout(timer);
  }, [load]);

  const pageQty = summary?.PageQty || 1;

  return (
    <React.Fragment>
      <Card>
        <CardHeader>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
            <div>
              <h5 className="card-title mb-1">
                Email Outbound{" "}
                <span className="text-muted fs-12">(MasterEmailOutbound)</span>
              </h5>
              <p className="text-muted mb-1 fs-13">{DESCRIPTION}</p>
              <div className="d-flex flex-wrap gap-1">
                    <Badge color="secondary">Read only</Badge>
              </div>
            </div>

          </div>

          <Row className="mt-3 g-2 align-items-end">
            <Col sm={6} md={5}>
              <label className="form-label mb-1 fs-13">Search</label>
              <Input
                type="text"
                placeholder="Search…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </Col>            <Col sm={6} md={2}>
              <label className="form-label mb-1 fs-13">Per page</label>
              <Input
                type="select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Input>
            </Col>
            <Col sm={6} md={3}>
              <div className="form-check mt-4">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="inc-deleted"
                  checked={includeDeleted}
                  onChange={(e) => {
                    setIncludeDeleted(e.target.checked);
                    setPage(1);
                  }}
                />
                <label className="form-check-label fs-13" htmlFor="inc-deleted">
                  Include deleted
                </label>
              </div>
            </Col>            <Col sm={6} md={2}>
              <Button
                color="light"
                className="w-100"
                onClick={load}
                disabled={loading}
              >
                Refresh
              </Button>
            </Col>
          </Row>

          {summary && !loading && (
            <p className="text-muted mb-0 mt-2 fs-13">
              {Number(summary.RowQty).toLocaleString()} row
              {summary.RowQty === 1 ? "" : "s"} — page {summary.Page} of{" "}
              {pageQty}
            </p>
          )}
        </CardHeader>

        <CardBody className="pt-0">
          {loading ? (
            <LoadingSpinner />
          ) : rows.length === 0 ? (
            <NoDataContent
              titleText="Nothing to show"
              paragraphText="No rows matched."
              useButton={false}
            />
          ) : (
            <>
              <div className="table-responsive table-card">
                <Table className="align-middle table-nowrap mb-0">
                  <thead className="text-muted">
                    <tr>
                      {COLUMNS.map((c) => (
                        <th key={c.key}>{c.key}</th>
                      ))}
                      
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={`${row["Id"] ?? "r"}-${i}`}>
                        {COLUMNS.map((c) => (
                          <td key={c.key}>{c.cell(row[c.key])}</td>
                        ))}

                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {pageQty > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <Button
                    color="light"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <span className="text-muted fs-13">
                    Page {page} of {pageQty}
                  </span>
                  <Button
                    color="light"
                    disabled={page >= pageQty}
                    onClick={() => setPage((p) => Math.min(pageQty, p + 1))}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

    </React.Fragment>
  );
};

export default MasterEmailOutbound;
